"use client";

import { type FC, useCallback, type ChangeEvent, useEffect, useState } from "react";
import { Content } from "./components";
import Card from "@components/shared/card/Card";
import Separator from "@/components/ui/separator";
import SnipeButton from "./components/SnipeButton";
import AddressInput from "./components/AddressInput";
import { createModalHelpers } from "@stores/unified-modal.store";
import { useSniper } from "@/stores/sniper.store";
import { useSnipesStore } from "@/stores/snipes.store";
import Currencies from "@components/shared/modals/Currencies";
import CurrencyForm from "../Swap/components/CurrencyForm";
import { useWallet } from "@/stores/wallet.store";
import History from "./components/history/History";
import AutoOption from "./components/AutoOption";
import BigSwitch from "@/components/ui/BigSwitch";
import SnipeList from "./components/SnipeList";
import SniperHeader from "@components/shared/card/SniperHeader";

const Sniper: FC = () => {
    const modalHelpers = createModalHelpers();
    const { openModal } = modalHelpers;
    const { isConnected, currentWallet } = useWallet();
    const isMnemonic = useWallet((s) => s.isMnemonic());
    const [amountError, setAmountError] = useState<string | null>(null);

    // Show select wallet modal ONLY when switching from mnemonic to non-mnemonic wallet and on Sniper page

    const {
        snipeAssetInfo,
        setSnipeAsset,
        setSniperAmount,
        sniperSpeed,
        setSniperSpeed,
        snipeTargetAddress,
        setSnipeTargetAddress,
        autobuy,
        setAutobuy,
        autosell,
        setAutosell,
        autoBuyAmount,
        setAutoBuyAmount,
        autoSellAmount,
        setAutoSellAmount,
        createSniper,
        updateSnipeAssetBalance,
        memePad,
        dex,
        setDex,
    } = useSniper();
    const { fetchSnipes, snipes, isLoading } = useSnipesStore(); // Get snipes state and fetch action

    // --- Ensure TON balance is fetched on first load ---
    useEffect(() => {
        if (isConnected && currentWallet?.guid && snipeAssetInfo.asset) {
            updateSnipeAssetBalance();
        }
    }, [isConnected, currentWallet?.guid, snipeAssetInfo.asset]);

    const handleSetSnipeCurrency = () => {
        openModal("currencies");
    };

    const handleAddressChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setSnipeTargetAddress(event.target.value);
    }, []);

    // Handle amount change with validation
    const handleAmountChange = (value: string) => {
        setSniperAmount(value);

        // Check if the asset is TON and validate minimum amount
        if (snipeAssetInfo.asset?.symbol === "TON") {
            const numValue = Number.parseFloat(value);
            if (!isNaN(numValue) && numValue < 0.5) {
                setAmountError("Amount cannot be less than 0.5 TON");
            } else {
                setAmountError(null);
            }
        } else {
            setAmountError(null);
        }
    };

    const speedOptions = [
        { value: "normal", label: "Normal" },
        { value: "fast", label: "Fast" },
        { value: "veryFast", label: "Ultra" },
    ];

    const poolsOptions = memePad
        ? [
              { value: "blumpump", label: "Blumpump" },
              { value: "gaspump", label: "Gaspump" },
          ]
        : [
              { value: "all", label: "All" },
              { value: "ston.fi_v2,ston.fi_v1", label: "Ston.fi" },
              { value: "dedust", label: "Dedust" },
          ];

    const handleSpeedChange = (speed: string) => {
        setSniperSpeed(speed);
    };

    const handleDexChange = (dex: string) => {
        setDex(dex);
    };

    // Fetch snipes when the component mounts and the wallet is connected
    useEffect(() => {
        if (!isConnected) return;
        // Poll snipes every 15 seconds to reduce backend/API load
        const interval = setInterval(() => {
            fetchSnipes();
        }, 15000); // 15 seconds to reduce backend/API load
        return () => clearInterval(interval);
    }, [isConnected, fetchSnipes]);

    // Only show loading spinner if loading and snipes are empty
    const showReloading = isLoading && snipes.length === 0;

    // Check if the amount is valid for the snipe button
    const isAmountValid =
        snipeAssetInfo.asset?.symbol !== "TON" ||
        Number.parseFloat(snipeAssetInfo.amount) >= 0.5 ||
        isNaN(Number.parseFloat(snipeAssetInfo.amount));

    return (
        <Content>
            <Currencies setAsset={setSnipeAsset} variant="Sell" />
            <History />
            <Card className="h-fit max-h-fit">
                <SniperHeader
                    handleReload={() => {
                        fetchSnipes();
                    }}
                    handleSettingsModal={() => openModal("Settings")}
                    isLoading={showReloading} // Pass as a prop if CardHeader supports it
                />
                {showReloading && (
                    <div className="flex justify-center items-center py-4">
                        <span className="text-gray-500 text-sm w-full">Loading snipes...</span>
                    </div>
                )}
                <Separator />
                <div
                    className={
                        "flex  gap-2 flex-col w-full transition-all duration-300 ease-in-out " +
                        (memePad ? "opacity-30 pointer-events-none" : "opacity-100")
                    }
                >
                    <div className="text-sm font-medium">Sniper Speed</div>
                    <BigSwitch
                        options={speedOptions}
                        value={sniperSpeed}
                        onValueChange={(value) => handleSpeedChange(value)}
                    />
                </div>
                <AddressInput
                    value={snipeTargetAddress}
                    onChange={handleAddressChange}
                    placeholder="EQAAA...AM9C"
                    name="targetAddress"
                    required
                    asset={snipeAssetInfo.asset ?? undefined}
                    label={memePad ? "Wallet address" : "Token or wallet address"}
                />
                <Separator variant="transparent" />

                {snipeAssetInfo.asset && (
                    <div className="flex flex-col w-full">
                        <CurrencyForm
                            title="Send"
                            asset={snipeAssetInfo.asset}
                            amount={snipeAssetInfo.amount}
                            setAmount={handleAmountChange}
                            balance={
                                isConnected && typeof snipeAssetInfo.balance === "number"
                                    ? snipeAssetInfo.balance.toFixed(snipeAssetInfo.balance < 0.0001 ? 8 : 4)
                                    : "connect wallet"
                            }
                            handleSetCurrency={handleSetSnipeCurrency}
                            price={snipeAssetInfo.price ?? undefined}
                            seletable={false}
                            percentButtons={true}
                        />
                        {amountError && <div className="text-red-500 text-xs mt-1 ml-1">{amountError}</div>}
                    </div>
                )}
                <BigSwitch options={poolsOptions} value={dex} onValueChange={(value) => handleDexChange(value)} />
                <div
                    className={
                        "flex  gap-3 flex-col w-full transition-all duration-300 ease-in-out " +
                        (memePad ? "opacity-30 pointer-events-none" : "opacity-100")
                    }
                >
                    <AutoOption
                        label="Auto-buy"
                        onChange={setAutobuy}
                        checked={autobuy}
                        value={Number(autoBuyAmount) || 0}
                        onValueChange={setAutoBuyAmount}
                    />

                    <AutoOption
                        label="Auto-sell"
                        onChange={setAutosell}
                        checked={autosell}
                        value={Number(autoSellAmount) || 0}
                        onValueChange={setAutoSellAmount}
                    />
                </div>
                <SnipeButton
                    createSniper={async () => {
                        try {
                            const result = await createSniper();
                            // Only show success toast if result is not an error
                            if (result && result.status !== "error") {
                                const { showToast } = await import("@/utils/showToast");
                                showToast("Sniper created successfully!", "success");
                            }
                        } catch (error: any) {
                            const { showToast } = await import("@/utils/showToast");
                            showToast(error?.message || "Failed to create sniper", "error");
                        }
                    }}
                    isMnemonic={isMnemonic}
                    noAddress={snipeTargetAddress === ""}
                    insufficientBalance={Number(snipeAssetInfo.amount) > snipeAssetInfo.balance}
                    noSendAmount={Number(snipeAssetInfo.amount) === 0}
                    invalidAmount={!isAmountValid}
                    onRequireMnemonicWallet={() => openModal("select")}
                />
            </Card>
            <SnipeList />
        </Content>
    );
};

export default Sniper;
